import { useQuery } from '@apollo/client'
import moment from 'moment'
import React from 'react'
import { Badge, Modal, Spinner, Button, Form } from 'react-bootstrap'
import { GET_CONTACT_COMMUNICATION_LOGS } from '../../../gql/queries/contactsApiQueries'

interface IModalProps {
  idContact: string
  isOpen: boolean
  onClose: () => void
}

export const ModalCommunicationLogs: React.FC<IModalProps> = ({ idContact, isOpen, onClose }) => {
  const { data, loading, error, refetch } = useQuery(GET_CONTACT_COMMUNICATION_LOGS, {
    variables: { contactId: idContact },
    skip: !isOpen || !idContact,
    fetchPolicy: 'network-only',
  })

  const [selectedChannels, setSelectedChannels] = React.useState<string[]>(['WHATSAPP', 'SMS', 'EMAIL', 'CALL'])
  const logs = React.useMemo(() => {
    return (data?.allCommunications?.edges || []).map((edge: any) => {
      if (edge?.node?.metadata && typeof edge.node.metadata === 'string') {
        try { return { ...edge, node: { ...edge.node, metadata: JSON.parse(edge.node.metadata) } } } catch {}
      }
      return edge
    })
  }, [data])

  const filteredAndSortedLogs = React.useMemo(() => {
    return [...logs]
      .filter(({ node }: any) => {
        const channel = node.channel?.toUpperCase() || 'OTHER'
        return selectedChannels.includes(channel)
      })
      .sort((a: any, b: any) => {
        return new Date(a.node.created).getTime() - new Date(b.node.created).getTime()
      })
  }, [logs, selectedChannels])

  const toggleChannel = (channel: string) => {
    if (selectedChannels.includes(channel)) {
      setSelectedChannels(selectedChannels.filter((c) => c !== channel))
    } else {
      setSelectedChannels([...selectedChannels, channel])
    }
  }

  const getStatusBadge = (status: string) => {
    const s = status.toUpperCase()
    if (s === 'SENT') return 'primary'
    if (s === 'DELIVERED') return 'info'
    if (s === 'READ') return 'success'
    if (s === 'FAILED') return 'danger'
    if (s === 'RECEIVED') return 'warning'
    if (s === 'COMPLETED') return 'success'
    return 'secondary'
  }

  const getChannelIcon = (channel: string) => {
    const c = channel.toUpperCase()
    if (c === 'WHATSAPP') return 'bi-whatsapp'
    if (c === 'SMS') return 'bi-chat-dots'
    if (c === 'EMAIL') return 'bi-envelope'
    if (c === 'CALL') return 'bi-telephone'
    return 'bi-chat'
  }

  const formatCallDuration = (seconds: number) => {
    if (!seconds) return '0s'
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return m > 0 ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`
  }

  return (
    <Modal className='modal-dialog-communication-logs' show={isOpen} onHide={onClose} size='lg' scrollable centered>
      <Modal.Header closeButton className='flexrow-between-wrap'>
        <Modal.Title className='m-0'>Communication Logs
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ minHeight: '300px' }}>
        <div className='flexrow-wrap-pill'>
          <div className='badge-pill-light'>
            <span className='text-muted fw-bold small text-uppercase' style={{ fontSize: '0.65rem' }}>Filter:</span>
            <Form.Check
              type='checkbox'
              id='modal-filter-whatsapp'
              label={<><i className='bi bi-whatsapp success ms-1'></i> WhatsApp</>}
              checked={selectedChannels.includes('WHATSAPP')}
              onChange={() => toggleChannel('WHATSAPP')}
              className='small fw-bold'
              style={{ fontSize: '0.75rem' }}
            />
            <Form.Check
              type='checkbox'
              id='modal-filter-sms'
              label={<><i className='bi bi-chat-dots ms-1 text-primary'></i> SMS</>}
              checked={selectedChannels.includes('SMS')}
              onChange={() => toggleChannel('SMS')}
              className='small fw-bold'
              style={{ fontSize: '0.75rem' }}
            />
            <Form.Check
              type='checkbox'
              id='modal-filter-call'
              label={<><i className='bi bi-telephone ms-1 text-info'></i> Calls</>}
              checked={selectedChannels.includes('CALL')}
              onChange={() => toggleChannel('CALL')}
              className='small fw-bold'
              style={{ fontSize: '0.75rem' }}
            />
          </div>
          <Button variant='secondary' size='sm' onClick={() => refetch()} className='text-secondary' title="Refresh">
            <i className='bi bi-arrow-clockwise fs-4'></i>
          </Button>
        </div>
        {loading ? (
          <div className='d-flex flex-column align-items-center justify-content-center h-100 p-5'>
            <Spinner animation='border' variant='primary' />
            <span className='mt-2 text-muted'>Loading logs...</span>
          </div>
        ) : error ? (
          <div className='alert alert-danger'>
            <i className='bi bi-exclamation-triangle-fill me-2'></i>
            Error loading communications: {error.message}
          </div>
        ) : logs.length === 0 ? (
          <div className='text-center p-5'>
            <i className='bi bi-chat-left-text text-muted fs-1 mb-3 d-block'></i>
            <p className='text-muted'>No communication logs found for this contact.</p>
          </div>
        ) : (
          <div className='d-flex flex-column gap-3'>
            {filteredAndSortedLogs.map(({ node }: any) => {
              const isOutbound = node.direction === 'OUTBOUND'
              return (
                <div
                  key={node.id}
                  className={`d-flex flex-column ${isOutbound ? 'align-items-end' : 'align-items-start'}`}
                >
                  <div
                    className='cardbox-message'
                    style={{
                      backgroundColor: node.channel?.toUpperCase() === 'CALL'
                        ? (isOutbound ? '#E8F0FE' : '#FFF3E0')
                        : node.channel?.toUpperCase() === 'WHATSAPP'
                          ? (isOutbound ? '#DCF8C6' : '#E7F3EF')
                          : (isOutbound ? '#F2F2F2' : '#FFFFFF'),
                      borderColor: node.channel?.toUpperCase() === 'CALL'
                        ? (isOutbound ? '#B3D4FC' : '#FFD9A0')
                        : node.channel?.toUpperCase() === 'WHATSAPP'
                          ? (isOutbound ? '#C7E9A0' : '#C1D6D0')
                          : (isOutbound ? '#D1D1D1' : '#E0E0E0'),
                      borderBottomRightRadius: isOutbound ? '0' : '8px',
                      borderBottomLeftRadius: isOutbound ? '8px' : '0',
                    }}
                  >
                    <div className='d-flex align-items-center gap-2 mb-1'>
                      <i className={`bi ${getChannelIcon(node.channel)} ${node.channel?.toUpperCase() === 'WHATSAPP' ? 'text-success' : node.channel?.toUpperCase() === 'CALL' ? 'text-info' : 'text-primary'} small`} />
                      <span className='fw-bold' style={{ fontSize: '0.7rem', textTransform: 'uppercase' }}>{node.channel}</span>
                      <Badge bg={getStatusBadge(node.status)} style={{ fontSize: '0.6rem' }} className='px-1'>
                        {node.status}
                      </Badge>
                    </div>

                    {node.channel?.toUpperCase() === 'CALL' ? (
                      <div className='small'>
                        <div className='d-flex flex-wrap gap-3 mb-1'>
                          {node.metadata?.disposition && (
                            <span><strong>Disposition:</strong> <Badge bg='warning' text='dark' className='ms-1'>{node.metadata.disposition}</Badge></span>
                          )}
                          {node.metadata?.talk_time_seconds > 0 && (
                            <span><strong>Duration:</strong> {formatCallDuration(node.metadata.talk_time_seconds)}</span>
                          )}
                          {node.metadata?.term_reason && (
                            <span><strong>Ended by:</strong> {node.metadata.term_reason}</span>
                          )}
                        </div>
                        {node.metadata?.phone && (
                          <div className='text-muted'><i className='bi bi-telephone me-1'></i>{node.metadata.phone}</div>
                        )}
                        {node.metadata?.recording_id && (
                          <div className='mt-1'>
                            <i className='bi bi-play-circle me-1 text-primary'></i>
                            <span className='text-muted small'>Recording #{node.metadata.recording_id}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className='small text-break' style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                        {node.content || <span className='fst-italic text-muted'>No content</span>}
                      </div>
                    )}

                    {node.metadata?.media_urls && Array.isArray(node.metadata.media_urls) && node.metadata.media_urls.length > 0 && (
                      <div className='mt-2 d-flex flex-column gap-1'>
                        {node.metadata.media_urls.map((url: string, idx: number) => {
                          const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(url)
                          return (
                            <div key={idx} className='border rounded p-1 bg-white-50 d-inline-block' style={{ maxWidth: 'fit-content' }}>
                              {isImage ? (
                                <a href={url} target='_blank' rel='noopener noreferrer'>
                                  <img src={url} alt="Attachment" style={{ maxHeight: '120px', maxWidth: '100%', cursor: 'pointer' }} className='rounded shadow-sm' />
                                </a>
                              ) : (
                                <a href={url} target='_blank' rel='noopener noreferrer' className='d-flex align-items-center gap-2 text-decoration-none py-1 px-2'>
                                  <i className='bi bi-file-earmark-arrow-down fs-5'></i>
                                  <span style={{ fontSize: '0.75rem' }}>Attachment {idx + 1}</span>
                                </a>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}

                    <div className='d-flex justify-content-end align-items-center gap-1 mt-1 opacity-50' style={{ fontSize: '0.65rem' }}>
                      <span>{moment(node.created).format('hh:mm A')}</span>
                      {isOutbound && (
                        <i className={`bi bi-check2-all ${node.status === 'READ' ? 'text-primary' : ''}`}></i>
                      )}
                    </div>
                  </div>

                  <div className='d-flex gap-2 px-1' style={{ fontSize: '0.65rem', opacity: 0.8 }}>
                    <span className='text-muted'>{moment(node.created).format('MMM DD, YYYY')}</span>
                    {node.user && isOutbound && (
                      <span className='text-muted'>• {node.user.firstName} {node.user.lastName}</span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose} size='sm'>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
